import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import Carousel from 'react-bootstrap/Carousel';

import styles from './carousel.scss';
import Controls from './CarouselControls';

/**
 * StyledCarousel Component
 *
 * A customizable carousel component with Bootstrap styling that supports
 * custom controls, fade transitions, and configurable intervals.
 *
 * ## Features
 * - Bootstrap carousel with custom controls
 * - Fade transitions support
 * - Configurable auto-play interval
 * - Custom controls component integration
 * - Keyboard navigation support
 *
 * ## Props
 * - `children` (Array<Element>): Array of React elements to display as slides (required)
 * - `className` (string): Additional CSS classes for the carousel
 * - `fade` (boolean): Enable fade transitions between slides (default: false)
 * - `id` (string): Unique identifier for the carousel
 * - `interval` (number): Auto-play interval in milliseconds (default: 5000)
 *
 * @component
 * @example
 * ```jsx
 * const slides = [
 *   <div key="1">Slide 1</div>,
 *   <div key="2">Slide 2</div>,
 *   <div key="3">Slide 3</div>
 * ];
 *
 * <StyledCarousel fade interval={3000}>
 *   {slides}
 * </StyledCarousel>
 * ```
 */
const StyledCarousel = ({
    children,
    className,
    fade = false,
    id = null,
    interval = null,
    bulletsInside = false,
    showArrows = true,
}) => {
    // State for current slide index
    const [index, setIndex] = React.useState(0);

    /**
     * Effect to reset index if children length changes and current index is out of bounds
     */
    React.useEffect(() => {
        if (index >= children.length && children.length > 0) {
            setIndex(children.length - 1);
        } else if (children.length === 0 && index !== 0) {
            setIndex(0);
        }
    }, [children.length, index]);

    /**
     * Handle carousel slide selection with bounds checking
     * @param {number} selectedIndex - The index of the selected slide
     */
    const handleCarouselSelect = React.useCallback((selectedIndex) => {
        let newIndex = selectedIndex;

        // Handle bounds checking - wrap around to valid indices
        if (newIndex < 0) {
            // Go to last element if going before first
            newIndex = children.length - 1;
        } else if (newIndex >= children.length) {
            // Go to first element if going beyond last
            newIndex = 0;
        }

        setIndex(newIndex);
    }, [children.length]);

    /**
     * Render custom controls component
     * Only rendered when there are multiple children
     */
    const renderControls = React.useCallback(() => {
        if (children.length <= 1) {
            return null;
        }

        return (
            <Controls
                currentIndex={index}
                items={children}
                onSelect={handleCarouselSelect}
                showArrows={showArrows}
                className={classNames({ 'bullets-inside': bulletsInside })}
            />
        );
    }, [index, children, handleCarouselSelect, showArrows, bulletsInside]);

    // Combine class names for the carousel
    const carouselClassName = React.useMemo(() => {
        return classNames(
            'mb-3',
            className && className.trim() ? className : '',
        );
    }, [className]);

    return (
        <div key={id} className={classNames(styles['styled-carousel'], className)}>
            <Carousel
                fade={fade}
                id={id}
                className={carouselClassName}
                activeIndex={index}
                indicators={false}
                interval={interval}
                controls={children.length > 1 && showArrows}
                onSelect={handleCarouselSelect}
            >
                {React.Children.toArray(children).map((child) => (
                    <Carousel.Item key={child.key}>
                        {child}
                    </Carousel.Item>
                ))}
            </Carousel>
            {renderControls()}
        </div>
    );
};

StyledCarousel.propTypes = {
    children: PropTypes.arrayOf(PropTypes.element).isRequired,
    className: PropTypes.string,
    fade: PropTypes.bool,
    id: PropTypes.string,
    interval: PropTypes.number,
    bulletsInside: PropTypes.bool,
    showArrows: PropTypes.bool,
};

export default React.memo(StyledCarousel);
